import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import { productGroups, doorConfigurations } from '../../data/products';
import { doorFamilies, configuratorOptions, leafGlassOptions } from '../../data/configurator';
import { leafGlassLabel } from '../../data/leafGlass';
import { useLanguage } from '../../i18n/LanguageContext';
import { useCart } from '../../contexts/CartContext';
import { trackEvent } from '../../analytics';
import { pageStyle, containerStyle, highlightStyle } from './pageStyles';

const findModel = (slug: string) =>
  productGroups.flatMap((g: any) => g.models).find((m: any) => m.slug === slug);

const findConfiguration = (model: any, configurationId: string) =>
  (model?.configurations || doorConfigurations).find((c: any) => c.id === configurationId);

const findFamily = (familyId: string) => doorFamilies.find((f) => f.id === familyId);
const optionName = (pick: any, id: string) => {
  const o = leafGlassOptions.find((x) => x.id === id) || configuratorOptions.find((x) => x.id === id);
  return o ? pick(o, 'name') : id;
};

const formatPrice = (v: number) =>
  `€${v.toLocaleString('et-EE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const inputStyle = {
  width: '100%',
  padding: '0.7rem 0.9rem',
  background: 'rgba(58, 58, 58, 0.4)',
  border: '1px solid rgba(223, 169, 38, 0.25)',
  borderRadius: '0.5rem',
  color: 'white',
  fontSize: '0.95rem'
};

const labelStyle = {
  display: 'block',
  color: '#9ca3af',
  fontSize: '0.8rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  marginBottom: '0.35rem'
};

const Cart = () => {
  const { t, pick, path, lang } = useLanguage();
  const glassText = (c: any) => {
    const label = leafGlassLabel(c.glass, c.glassPattern, lang) || optionName(pick, c.glass);
    return c.glassSurcharge == null ? label : `${label} · ${c.glassSurcharge === 0
      ? (lang === 'et' ? 'hinnas' : 'included')
      : `+${formatPrice(c.glassSurcharge)} ${lang === 'et' ? 'ukse kohta' : 'per door'}`}`;
  };
  const panelText = (c: any) => (c.cottagePattern || c.leafGeometry?.cottagePattern) === 'vertical'
    ? (lang === 'et' ? 'Püstised paneelid' : 'Vertical panels')
    : (lang === 'et' ? 'Kuusemuster' : 'Chevron panels');
  // Custom NCS/RAL colours always carry their code on the order — an empty
  // code is written out as "to be confirmed" so the invoice is never silent.
  const colourText = (c: any) => {
    const name = optionName(pick, c.colour);
    const isCustom = c.colour === 'colour_custom_ncs' || c.colour === 'colour_custom_ral';
    return isCustom ? `${name}: ${c.colourCode || t('configurator.colourCode.tbc')}` : name;
  };
  const { items, updateQty, removeItem, updateConfiguredQty, removeConfiguredItem, clearCart, totalItems } = useCart();
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', country: '', address: '', message: ''
  });
  const [sent, setSent] = useState(false);

  const handleChange = (e: any) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const lines = items.map((item: any) => {
      if (item.configured) {
        const c = item.config;
        const fam = findFamily(c.family);
        const parts = [
          c.dimensions ? `${c.dimensions.width_mm}x${c.dimensions.height_mm} mm` : null,
          c.design && optionName(pick, c.design),
          c.leafWindowLayout === 'three_windows' && t('configurator.leafWindows.three'),
          c.family === 'COTTAGE' && panelText(c),
          c.colour && colourText(c),
          c.glass && glassText(c),
          c.frame && optionName(pick, c.frame),
          c.threshold && optionName(pick, c.threshold),
          c.lock && optionName(pick, c.lock),
          ...(c.extras || []).map((x: string) => optionName(pick, x)),
          c.opening, c.handing,
          c.unitPrice != null ? `est. ${formatPrice(c.unitPrice)}${c.requiresReview ? ' (tbc)' : ''}` : 'price tbc'
        ].filter(Boolean);
        return `- ${pick(fam, 'name')} [${c.family}] — ${parts.join(', ')} — ${t('cart.qty')}: ${item.qty}`;
      }
      const model = findModel(item.slug);
      const config = findConfiguration(model, item.configurationId);
      return `- ${pick(model, 'name')} (${model?.code}) — ${pick(config, 'name')} — ${t('cart.qty')}: ${item.qty}`;
    });
    const subject = encodeURIComponent(`${t('cart.mailSubject')} — ${form.name}`);
    const body = encodeURIComponent(
      `${t('cart.mailBodyItems')}:\n${lines.join('\n')}\n\n` +
      `${t('contact.mailBodyName')}: ${form.name}\n` +
      `${t('contact.mailBodyCompany')}: ${form.company}\n` +
      `${t('contact.mailBodyEmail')}: ${form.email}\n` +
      `${t('contact.mailBodyPhone')}: ${form.phone}\n` +
      `${t('contact.mailBodyCountry')}: ${form.country}\n` +
      `${t('cart.mailBodyAddress')}: ${form.address}\n\n${form.message}`
    );
    window.location.href = `mailto:info@hestiadoors.com?subject=${subject}&body=${body}`;
    trackEvent('order_request_submit', { items: totalItems, products: items.map((i: any) => i.slug).join(',') });
    // Keep the cart: mailto only opens the visitor's email app, so if none is
    // configured the order would otherwise be lost. They clear it themselves.
    setSent(true);
  };

  return (
    <div style={pageStyle}>
      <SEO
        title={`${t('cart.title')} | Hestia Doors`}
        description={t('cart.metaDesc')}
        canonicalPath="/cart"
        noindex
      />
      <Navigation />

      <div style={containerStyle}>
        <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
          {t('cart.title')}
        </h1>
        <p style={{ color: '#9ca3af', maxWidth: '42rem', marginBottom: '2.5rem', lineHeight: 1.7 }}>
          {t('cart.intro')}
        </p>

        {sent && (
          <div style={{ border: '1px solid rgba(223, 169, 38, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '2rem', background: 'rgba(223, 169, 38, 0.08)' }}>
            <h2 style={{ ...highlightStyle, fontSize: '1.25rem', marginBottom: '0.5rem' }}>{t('cart.sentTitle')}</h2>
            <p style={{ color: '#d1d5db', margin: 0, lineHeight: 1.6 }}>{t('cart.sentDesc')}</p>
            {items.length > 0 && (
              <button
                onClick={clearCart}
                style={{ marginTop: '1rem', background: 'none', border: '1px solid #dfa926', color: '#dfa926', borderRadius: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                {t('cart.clearCart')}
              </button>
            )}
          </div>
        )}

        {items.length === 0 && !sent ? (
          <div style={{ marginBottom: '3rem' }}>
            <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>{t('cart.empty')}</p>
            <Link
              to={path('/products')}
              style={{ backgroundColor: '#dfa926', color: 'black', fontWeight: 600, padding: '0.85rem 1.75rem', borderRadius: '0.5rem', textDecoration: 'none' }}
            >
              {t('cart.browseProducts')}
            </Link>
          </div>
        ) : items.length > 0 && (
          <>
            <div style={{ marginBottom: '3rem' }}>
              {items.map((item: any) => {
                const rowKey = item.configured ? item.cid : `${item.slug}::${item.configurationId}`;
                const model = item.configured ? null : findModel(item.slug);
                const config = item.configured ? null : findConfiguration(model, item.configurationId);
                const fam = item.configured ? findFamily(item.config.family) : null;
                if (!item.configured && !model) return null;
                const qtyDelta = (d: number) =>
                  item.configured
                    ? updateConfiguredQty(item.cid, item.qty + d)
                    : updateQty(item.slug, item.configurationId, item.qty + d);
                const remove = () =>
                  item.configured
                    ? removeConfiguredItem(item.cid)
                    : removeItem(item.slug, item.configurationId);
                const c = item.config;
                return (
                  <div
                    key={rowKey}
                    style={{
                      display: 'flex', gap: '1.25rem', alignItems: 'center',
                      border: '1px solid rgba(58, 58, 58, 0.6)', borderRadius: '0.75rem',
                      padding: '1.25rem', marginBottom: '1rem',
                      background: 'rgba(26, 26, 26, 0.5)', flexWrap: 'wrap' as const
                    }}
                  >
                    {model && (
                      <img src={model.image} alt={pick(model, 'name')} style={{ width: '5rem', height: '5rem', objectFit: 'contain', borderRadius: '0.5rem', background: 'linear-gradient(45deg, #3a3a3a, #1a1a1a)' }} />
                    )}
                    <div style={{ flex: 1, minWidth: '12rem' }}>
                      {item.configured ? (
                        <>
                          <Link to={path('/configurator')} style={{ color: 'white', fontWeight: 600, textDecoration: 'none', fontSize: '1.05rem' }}>
                            {pick(fam, 'name')} — {t('cart.configuredDoor')}
                          </Link>
                          <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '0.25rem', lineHeight: 1.6 }}>
                            {c.dimensions.width_mm}×{c.dimensions.height_mm} mm
                            {c.glass && ` · ${glassText(c)}`}
                            {[c.design, c.frame, c.threshold, c.lock, ...(c.extras || [])]
                              .filter(Boolean).map((id: string) => ` · ${optionName(pick, id)}`).join('')}
                            {c.leafWindowLayout === 'three_windows' && ` · ${t('configurator.leafWindows.three')}`}
                            {c.family === 'COTTAGE' && ` · ${panelText(c)}`}
                            {c.colour && ` · ${colourText(c)}`}
                            {` · ${c.opening} · ${c.handing}`}
                          </div>
                          <div style={{ fontSize: '0.85rem', marginTop: '0.35rem', color: c.unitPrice != null ? '#dfa926' : '#9ca3af' }}>
                            {c.unitPrice != null && !c.requiresReview && `${formatPrice(c.unitPrice)} / ${t('cart.perUnit')}`}
                            {c.unitPrice != null && c.requiresReview && `${t('cart.estimated')} ${formatPrice(c.unitPrice)} — ${t('cart.priceReviewNote')}`}
                            {c.unitPrice == null && t('cart.priceUnconfirmed')}
                          </div>
                        </>
                      ) : (
                        <>
                          <Link to={path(`/products/${item.slug}`)} style={{ color: 'white', fontWeight: 600, textDecoration: 'none', fontSize: '1.05rem' }}>
                            {pick(model, 'name')}
                          </Link>
                          <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                            {t('productPage.modelCode')}: {model.code} · {config ? pick(config, 'name') : item.configurationId}
                          </div>
                        </>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button
                        onClick={() => qtyDelta(-1)}
                        aria-label={t('cart.decrease')}
                        style={{ width: '2rem', height: '2rem', background: 'none', border: '1px solid #dfa926', color: '#dfa926', borderRadius: '0.35rem', cursor: 'pointer', fontSize: '1rem' }}
                      >−</button>
                      <span style={{ color: 'white', minWidth: '2rem', textAlign: 'center' as const }}>{item.qty}</span>
                      <button
                        onClick={() => qtyDelta(1)}
                        aria-label={t('cart.increase')}
                        style={{ width: '2rem', height: '2rem', background: 'none', border: '1px solid #dfa926', color: '#dfa926', borderRadius: '0.35rem', cursor: 'pointer', fontSize: '1rem' }}
                      >+</button>
                    </div>
                    <button
                      onClick={remove}
                      style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}
                    >
                      {t('cart.remove')}
                    </button>
                  </div>
                );
              })}
              <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                {t('cart.totalItems')}: {totalItems}
              </div>
            </div>

            <section style={{ maxWidth: '42rem', marginBottom: '4rem' }}>
              <h2 style={{ ...highlightStyle, fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                {t('cart.orderTitle')}
              </h2>
              <p style={{ color: '#9ca3af', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                {t('cart.orderDesc')}
              </p>
              <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label htmlFor="cart-name" style={labelStyle}>{t('cart.fieldName')} *</label>
                    <input id="cart-name" name="name" required value={form.name} onChange={handleChange} style={inputStyle} />
                  </div>
                  <div>
                    <label htmlFor="cart-company" style={labelStyle}>{t('contact.company')}</label>
                    <input id="cart-company" name="company" value={form.company} onChange={handleChange} style={inputStyle} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label htmlFor="cart-email" style={labelStyle}>{t('contact.email')} *</label>
                    <input id="cart-email" name="email" type="email" required value={form.email} onChange={handleChange} style={inputStyle} />
                  </div>
                  <div>
                    <label htmlFor="cart-phone" style={labelStyle}>{t('contact.phone')}</label>
                    <input id="cart-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} style={inputStyle} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label htmlFor="cart-country" style={labelStyle}>{t('contact.country')}</label>
                    <input id="cart-country" name="country" value={form.country} onChange={handleChange} style={inputStyle} />
                  </div>
                  <div>
                    <label htmlFor="cart-address" style={labelStyle}>{t('cart.fieldAddress')}</label>
                    <input id="cart-address" name="address" value={form.address} onChange={handleChange} style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label htmlFor="cart-message" style={labelStyle}>{t('cart.fieldMessage')}</label>
                  <textarea id="cart-message" name="message" rows={4} value={form.message} onChange={handleChange} style={{ ...inputStyle, resize: 'vertical' as const }} />
                </div>
                <button
                  type="submit"
                  style={{ backgroundColor: '#dfa926', color: 'black', fontWeight: 600, padding: '0.85rem 1.75rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem', justifySelf: 'start' }}
                >
                  {t('cart.submitOrder')}
                </button>
                <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: 0 }}>{t('cart.invoiceNote')}</p>
              </form>
            </section>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Cart;
